/**
 * @file SystemHubConfiguracoes.jsx
 *
 * Seção de status de manutenção refatorada:
 *  - Reflete automaticamente GMUDs "Em Execução"
 *  - Ativação manual exige data/hora de término
 *  - Modo manual usa 'page' (MaintenancePage + cronômetro) por padrão
 *  - Painel de ativação inline com display_mode toggle
 *  - Mini-countdown ao vivo no status card
 *  - Estado travado quando origem é GMUD
 *  - [NOVO] Seção "Janelas em Andamento": GMUDs que já começaram e não foram concluídas
 *
 * Backend esperado em GET /maintenance-status:
 * {
 *   maintenanceMode: bool,
 *   source: 'manual' | 'gmud' | null,
 *   endDate: ISO string | null,
 *   displayMode: 'message' | 'page',
 *   activeGmudId: number | null
 * }
 *
 * Backend esperado em GET /maintenance/schedules/in-progress:
 * [ { id, titulo, status, tipo, janela_inicio, janela_fim, solicitante_nome } ]
 * Retorna GMUDs onde janela_inicio <= NOW() e status NOT IN ('Concluída','Finalizada','Cancelada')
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import './SystemHubConfiguracoes.css';
import {
    FaTools, FaExclamationTriangle, FaCheckCircle,
    FaCalendarPlus, FaTrash, FaSpinner, FaClock,
    FaRegCalendarAlt, FaShieldAlt, FaLock,
    FaChevronDown, FaGlobeAmericas, FaBuilding,
    FaClipboardList, FaExclamationCircle, FaUndo,
    FaCommentAlt, FaDesktop, FaTimes, FaPlay,
    FaStop, FaInfoCircle, FaLink, FaHourglassHalf,
} from 'react-icons/fa';

// ─── GMUD Options ────────────────────────────────────────────────────────────
const GMUD_OPTIONS = {
    JUSTIFICATIVA: [
        { value: 'Atualização de Versão',    label: 'Atualização de Versão Principal (Major Update)' },
        { value: 'Patch de Segurança',       label: 'Patch de Segurança Urgente' },
        { value: 'Correção Crítica',         label: 'Correção de Bug Crítico (Hotfix)' },
        { value: 'Melhoria de Performance',  label: 'Otimização e Melhoria de Performance' },
        { value: 'Outros',                   label: 'Outros (descrever abaixo)' },
    ],
    IMPACTO: [
        { value: 'Indisponibilidade Total',    label: 'Indisponibilidade total do sistema' },
        { value: 'Indisponibilidade Parcial',  label: 'Indisponibilidade parcial de módulos' },
        { value: 'Degradação Temporária',      label: 'Degradação temporária de performance' },
        { value: 'Nenhum Impacto',             label: 'Nenhum impacto perceptível' },
        { value: 'Outros',                     label: 'Outros (descrever abaixo)' },
    ],
    ROLLBACK: [
        { value: 'Restaurar Backup Completo',  label: 'Restaurar backup completo (DB + Código) D-1' },
        { value: 'Reverter Commit de Código',  label: 'Reverter commit na branch principal' },
        { value: 'Nenhum Rollback Necessário', label: 'Sem rollback (mudança não destrutiva)' },
        { value: 'Outros',                     label: 'Outros (descrever abaixo)' },
    ]
};

const INITIAL_FORM = {
    data_inicio: '', data_fim: '', cliente_id: 'todos', motivo: '',
    display_mode: 'message',
    gmud_justificativa_select: GMUD_OPTIONS.JUSTIFICATIVA[0].value,
    gmud_justificativa_text: '',
    gmud_impacto_select: GMUD_OPTIONS.IMPACTO[0].value,
    gmud_impacto_text: '',
    gmud_rollback_select: GMUD_OPTIONS.ROLLBACK[0].value,
    gmud_rollback_text: '',
};

// ─── GMUD status style map ────────────────────────────────────────────────────
const GMUD_STATUS_STYLE = {
    'Pendente':    { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' },
    'Aprovada':    { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
    'Em Execução': { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
};

// ─── Hook: mini countdown ao vivo ───────────────────────────────────────────
function useMiniCountdown(endDate) {
    const [diff, setDiff] = useState(0);

    useEffect(() => {
        if (!endDate) { setDiff(0); return; }
        const update = () => setDiff(Math.max(0, +new Date(endDate) - Date.now()));
        update();
        const id = setInterval(update, 1000);
        return () => clearInterval(id);
    }, [endDate]);

    if (!endDate || diff <= 0) return null;

    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return d > 0
        ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`
        : `${pad(h)}:${pad(m)}:${pad(s)}`;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtDateTime = (d) => d
    ? new Date(d).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    })
    : '—';

const fmtDateTimeFull = (d) => d
    ? new Date(d).toLocaleString('pt-BR', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    })
    : '—';

// Mínimo para o datetime-local: agora + 5 min
const minEndDate = () => {
    const d = new Date(Date.now() + 5 * 60000);
    return d.toISOString().slice(0, 16);
};

// ─── SectionHeader ────────────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, subtitle, badge }) => (
    <div className="shc-section-header">
        <div className="shc-section-title-wrap">
            <div className="shc-section-icon"><Icon size={15} /></div>
            <div>
                <h3 className="shc-section-title">{title}</h3>
                {subtitle && <p className="shc-section-subtitle">{subtitle}</p>}
            </div>
        </div>
        {badge && <span className="shc-badge">{badge}</span>}
    </div>
);

// ─── GmudSelect ──────────────────────────────────────────────────────────────
const GmudSelect = ({ label, icon: Icon, selectName, textName, value, textValue, options, onChange, loading }) => (
    <div className="shc-gmud-field">
        <label className="shc-label">{Icon && <Icon size={12} />} {label}</label>
        <div className="shc-select-wrap">
            <select name={selectName} value={value} onChange={onChange} disabled={loading} className="shc-select">
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <FaChevronDown size={11} className="shc-select-arrow" />
        </div>
        {value === 'Outros' && (
            <textarea name={textName} rows={3} value={textValue} onChange={onChange}
                placeholder="Descreva com detalhes…" disabled={loading} required
                className="shc-textarea shc-textarea--expanded" />
        )}
    </div>
);

// ─── DisplayModeToggle ───────────────────────────────────────────────────────
const DisplayModeToggle = ({ value, onChange, disabled, compact }) => (
    <div className={`shc-field ${compact ? '' : 'shc-field--full'}`}>
        {!compact && (
            <label className="shc-label"><FaDesktop size={12} /> Modo de exibição durante a manutenção</label>
        )}
        <div className={`shc-display-toggle ${compact ? 'shc-display-toggle--compact' : ''}`}>
            <button type="button" disabled={disabled} onClick={() => onChange('message')}
                className={`shc-display-btn ${value === 'message' ? 'active' : ''}`}>
                <div className="shc-display-btn-icon shc-display-btn-icon--msg">
                    <FaCommentAlt size={compact ? 14 : 18} />
                </div>
                <div className="shc-display-btn-text">
                    <strong>Mensagem no login</strong>
                    {!compact && <span>Aviso discreto no formulário de login</span>}
                </div>
                <div className={`shc-display-btn-dot ${value === 'message' ? 'active' : ''}`} />
            </button>
            <button type="button" disabled={disabled} onClick={() => onChange('page')}
                className={`shc-display-btn ${value === 'page' ? 'active' : ''}`}>
                <div className="shc-display-btn-icon shc-display-btn-icon--page">
                    <FaDesktop size={compact ? 14 : 18} />
                </div>
                <div className="shc-display-btn-text">
                    <strong>Página completa</strong>
                    {!compact && <span>Tela imersiva com cronômetro regressivo</span>}
                </div>
                <div className={`shc-display-btn-dot ${value === 'page' ? 'active' : ''}`} />
            </button>
        </div>
    </div>
);

// ─── ScheduleItem ────────────────────────────────────────────────────────────
const ScheduleItem = ({ ag, clientes, onCancel, loading }) => {
    const isGlobal = ag.cliente_id === 'todos' || ag.cliente_id === null;
    const clienteNome = isGlobal
        ? 'Global — todos os clientes'
        : clientes.find(c => c.id === ag.cliente_id)?.nome_empresa || 'Cliente não encontrado';
    const diffMs = +new Date(ag.data_fim) - Date.now();
    const expired = diffMs <= 0;
    const hoursLeft = Math.max(0, Math.floor(diffMs / 3600000));
    const isPage = ag.display_mode === 'page';

    return (
        <div className={`shc-schedule-item ${expired ? 'shc-schedule-item--expired' : ''}`}>
            <div className="shc-schedule-dot" />
            <div className="shc-schedule-body">
                <div className="shc-schedule-top">
                    <span className="shc-schedule-client">
                        {isGlobal
                            ? <><FaGlobeAmericas size={12} /> {clienteNome}</>
                            : <><FaBuilding size={12} /> {clienteNome}</>}
                    </span>
                    <span className={`shc-mode-badge ${isPage ? 'shc-mode-badge--page' : 'shc-mode-badge--msg'}`}>
                        {isPage ? <><FaDesktop size={10} /> Tela completa</> : <><FaCommentAlt size={10} /> Mensagem</>}
                    </span>
                    {!expired && (
                        <span className="shc-schedule-badge-time">
                            <FaClock size={10} />
                            {hoursLeft < 1 ? 'em menos de 1h' : `em ~${hoursLeft}h`}
                        </span>
                    )}
                    {expired && <span className="shc-schedule-badge-expired">expirado</span>}
                </div>
                <p className="shc-schedule-reason">{ag.motivo}</p>
                <div className="shc-schedule-times">
                    <span><strong>Início:</strong> {fmtDateTimeFull(ag.data_inicio)}</span>
                    <span className="shc-schedule-sep">→</span>
                    <span><strong>Fim:</strong> {fmtDateTimeFull(ag.data_fim)}</span>
                </div>
            </div>
            <button onClick={() => onCancel(ag.id)} disabled={loading} className="shc-delete-btn" title="Cancelar">
                <FaTrash size={13} />
            </button>
        </div>
    );
};

// ─── InProgressItem ──────────────────────────────────────────────────────────
const InProgressItem = ({ gmud }) => {
    const style = GMUD_STATUS_STYLE[gmud.status] || GMUD_STATUS_STYLE['Pendente'];
    const isRunning = gmud.status === 'Em Execução';

    return (
        <div
            className="shc-schedule-item"
            style={{ borderLeft: isRunning ? '3px solid #ea580c' : '3px solid transparent' }}
        >
            <div
                className="shc-schedule-dot"
                style={{
                    background: style.color,
                    boxShadow: isRunning ? `0 0 0 3px ${style.bg}` : undefined,
                }}
            />
            <div className="shc-schedule-body">
                <div className="shc-schedule-top">
                    <span className="shc-schedule-client" style={{ fontWeight: 700 }}>
                        {gmud.titulo}
                    </span>
                    <span
                        className="shc-mode-badge"
                        style={{
                            background: style.bg,
                            color: style.color,
                            border: `1px solid ${style.border}`,
                        }}
                    >
                        {isRunning && <FaPlay size={9} />} {gmud.status}
                    </span>
                    <span className="shc-schedule-badge-time">
                        <FaLink size={10} /> GMUD #{gmud.id}
                    </span>
                </div>
                <p className="shc-schedule-reason">
                    Solicitante: {gmud.solicitante_nome}
                </p>
                <div className="shc-schedule-times">
                    <span><strong>Início:</strong> {fmtDateTimeFull(gmud.janela_inicio)}</span>
                    <span className="shc-schedule-sep">→</span>
                    <span><strong>Fim previsto:</strong> {fmtDateTimeFull(gmud.janela_fim)}</span>
                </div>
            </div>
        </div>
    );
};

// ─── StatusCard ──────────────────────────────────────────────────────────────
const StatusCard = ({ status, onActivate, onDeactivate, loading }) => {
    const { isActive, source, endDate, displayMode, activeGmudId } = status;

    const [panelOpen, setPanelOpen] = useState(false);
    const [panelEndDate, setPanelEndDate] = useState('');
    const [panelMode, setPanelMode] = useState('page');
    const [panelError, setPanelError] = useState('');
    const endDateRef = useRef(null);

    const countdown = useMiniCountdown(endDate);

    const openPanel = () => {
        setPanelOpen(true);
        setPanelEndDate('');
        setPanelMode('page');
        setPanelError('');
        setTimeout(() => endDateRef.current?.focus(), 50);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setPanelEndDate('');
        setPanelError('');
    };

    const handleConfirmActivate = () => {
        if (!panelEndDate) { setPanelError('Informe a data e hora de término.'); return; }
        if (new Date(panelEndDate) <= new Date(Date.now() + 4 * 60000)) {
            setPanelError('A data de término deve ser pelo menos 5 minutos no futuro.'); return;
        }
        setPanelError('');
        onActivate(panelEndDate, panelMode);
        closePanel();
    };

    const isFromGmud = isActive && source === 'gmud';
    const isManual   = isActive && source === 'manual';

    return (
        <div className="shc-status-card-wrap">
            <div className={`shc-status-bar ${isActive ? (isFromGmud ? 'shc-status-bar--gmud' : 'shc-status-bar--active') : 'shc-status-bar--ok'}`}>
                <div className="shc-status-left">
                    <div className="shc-status-pulse">
                        {isFromGmud
                            ? <FaLock size={18} />
                            : isManual
                                ? <FaExclamationTriangle size={18} />
                                : <FaCheckCircle size={18} />
                        }
                    </div>

                    <div className="shc-status-texts">
                        <div className="shc-status-title-row">
                            <p className="shc-status-title">
                                Modo Manutenção Global —{' '}
                                <strong>{isActive ? 'ATIVADO' : 'DESATIVADO'}</strong>
                            </p>
                            {isFromGmud && (
                                <span className="shc-source-badge shc-source-badge--gmud">
                                    <FaLink size={9} /> GMUD #{activeGmudId}
                                </span>
                            )}
                            {isManual && (
                                <span className="shc-source-badge shc-source-badge--manual">
                                    <FaPlay size={9} /> Ativação manual
                                </span>
                            )}
                        </div>

                        <p className="shc-status-desc">
                            {isFromGmud && (
                                <>Gerenciado automaticamente pela <strong>GMUD #{activeGmudId}</strong>. Apenas a conclusão da GMUD desativará este modo.</>
                            )}
                            {isManual && (
                                <>Ativado manualmente. Término previsto: <strong>{fmtDateTime(endDate)}</strong>.</>
                            )}
                            {!isActive && (
                                <>Sistema operando normalmente. Todos os usuários têm acesso liberado.</>
                            )}
                        </p>

                        {isActive && (
                            <div className="shc-status-meta">
                                <span className={`shc-mode-pill ${displayMode === 'page' ? 'shc-mode-pill--page' : 'shc-mode-pill--msg'}`}>
                                    {displayMode === 'page'
                                        ? <><FaDesktop size={10} /> Página completa</>
                                        : <><FaCommentAlt size={10} /> Mensagem no login</>
                                    }
                                </span>
                                {countdown && (
                                    <span className="shc-live-countdown">
                                        <FaClock size={10} />
                                        <span className="shc-live-countdown__time">{countdown}</span>
                                        <span className="shc-live-countdown__label">restante</span>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="shc-status-actions">
                    {!isActive && !panelOpen && (
                        <button onClick={openPanel} disabled={loading}
                            className="shc-toggle-btn shc-toggle-btn--enable">
                            <FaPlay size={12} /> Ativar Manualmente
                        </button>
                    )}
                    {!isActive && panelOpen && (
                        <button onClick={closePanel} className="shc-toggle-btn shc-toggle-btn--ghost">
                            <FaTimes size={12} /> Cancelar
                        </button>
                    )}
                    {isManual && (
                        <button onClick={onDeactivate} disabled={loading}
                            className="shc-toggle-btn shc-toggle-btn--disable">
                            {loading
                                ? <><FaSpinner className="shc-spin" size={12} /> Processando…</>
                                : <><FaStop size={12} /> Desativar Manutenção</>
                            }
                        </button>
                    )}
                    {isFromGmud && (
                        <button disabled className="shc-toggle-btn shc-toggle-btn--locked" title="Gerenciado pela GMUD">
                            <FaLock size={12} /> Gerenciado pela GMUD
                        </button>
                    )}
                </div>
            </div>

            {panelOpen && (
                <div className="shc-activation-panel">
                    <div className="shc-activation-panel__header">
                        <FaExclamationTriangle size={14} className="shc-activation-panel__warn-icon" />
                        <div>
                            <strong>Ativar modo de manutenção manual</strong>
                            <span>Defina até quando o sistema ficará indisponível e como os usuários serão informados.</span>
                        </div>
                    </div>

                    <div className="shc-activation-panel__fields">
                        <div className="shc-field">
                            <label className="shc-label">
                                <FaClock size={12} /> Data e hora de término <span className="shc-required">*</span>
                            </label>
                            <input
                                ref={endDateRef}
                                type="datetime-local"
                                value={panelEndDate}
                                min={minEndDate()}
                                onChange={e => { setPanelEndDate(e.target.value); setPanelError(''); }}
                                className={`shc-input ${panelError ? 'shc-input--error' : ''}`}
                            />
                            {panelError && (
                                <span className="shc-field-error">
                                    <FaInfoCircle size={11} /> {panelError}
                                </span>
                            )}
                        </div>

                        <div className="shc-field">
                            <label className="shc-label"><FaDesktop size={12} /> Modo de exibição para os usuários</label>
                            <DisplayModeToggle value={panelMode} onChange={setPanelMode} compact />
                        </div>
                    </div>

                    <div className="shc-activation-panel__info">
                        <FaInfoCircle size={12} />
                        <span>
                            {panelMode === 'page'
                                ? 'Usuários serão redirecionados para a tela de manutenção com cronômetro regressivo.'
                                : 'Usuários verão um aviso no formulário de login, mas poderão tentar acessar o sistema.'}
                        </span>
                    </div>

                    <div className="shc-activation-panel__actions">
                        <button type="button" onClick={closePanel} className="shc-btn-ghost">
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirmActivate}
                            disabled={loading || !panelEndDate}
                            className="shc-btn-activate"
                        >
                            {loading
                                ? <><FaSpinner className="shc-spin" size={13} /> Ativando…</>
                                : <><FaPlay size={12} /> Confirmar Ativação</>
                            }
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SystemHubConfiguracoes() {
    const [maintenanceStatus, setMaintenanceStatus] = useState({
        isActive: false,
        source: null,
        endDate: null,
        displayMode: 'page',
        activeGmudId: null,
    });
    const [loading, setLoading]             = useState(true);
    const [clientes, setClientes]           = useState([]);
    const [agendamentos, setAgendamentos]   = useState([]);
    const [gmudEmAndamento, setGmudEmAndamento] = useState([]);
    const [form, setForm]                   = useState(INITIAL_FORM);
    const [submitting, setSubmitting]       = useState(false);

    // ── Fetch ──
    const fetchStatus = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: s }, { data: c }, { data: a }, { data: ip }] = await Promise.all([
                api.get('/api/system-hub/maintenance-status'),
                api.get('/api/system-hub/clientes'),
                api.get('/api/system-hub/maintenance/schedules'),
                api.get('/api/system-hub/maintenance/schedules/in-progress'),
            ]);

            setMaintenanceStatus({
                isActive:     s.maintenanceMode  ?? false,
                source:       s.source           ?? null,
                endDate:      s.endDate          ?? null,
                displayMode:  s.displayMode      ?? 'page',
                activeGmudId: s.activeGmudId     ?? null,
            });
            setClientes(c);
            setAgendamentos(a);
            setGmudEmAndamento(ip);
        } catch {
            toast.error('Falha ao carregar dados de manutenção.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    // ── Ativar manualmente ──
    const handleActivate = useCallback(async (endDate, displayMode) => {
        setLoading(true);
        try {
            await api.put('/api/system-hub/settings/maintenance', {
                status: true,
                endDate: new Date(endDate).toISOString(),
                displayMode,
            });
            toast.success('Modo manutenção ativado com sucesso!');
            fetchStatus();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao ativar modo manutenção.');
            setLoading(false);
        }
    }, [fetchStatus]);

    // ── Desativar ──
    const handleDeactivate = useCallback(async () => {
        if (!window.confirm('Deseja DESATIVAR o modo manutenção agora?')) return;
        setLoading(true);
        try {
            await api.put('/api/system-hub/settings/maintenance', { status: false });
            toast.success('Modo manutenção desativado!');
            fetchStatus();
        } catch {
            toast.error('Erro ao desativar modo de manutenção.');
            setLoading(false);
        }
    }, [fetchStatus]);

    // ── Formulário de agendamento ──
    const handleFormChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleDisplayMode = (mode) => setForm(prev => ({ ...prev, display_mode: mode }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        const {
            gmud_justificativa_select, gmud_justificativa_text,
            gmud_impacto_select, gmud_impacto_text,
            gmud_rollback_select, gmud_rollback_text,
            display_mode, ...rest
        } = form;

        const finalJ = gmud_justificativa_select === 'Outros' ? gmud_justificativa_text.trim() : gmud_justificativa_select;
        const finalI = gmud_impacto_select === 'Outros'       ? gmud_impacto_text.trim()       : gmud_impacto_select;
        const finalR = gmud_rollback_select === 'Outros'      ? gmud_rollback_text.trim()      : gmud_rollback_select;

        if (!form.data_inicio || !form.data_fim || !form.motivo.trim()) {
            toast.warn('Preencha data de início, fim e título.'); return;
        }
        if (
            (gmud_justificativa_select === 'Outros' && !finalJ) ||
            (gmud_impacto_select === 'Outros' && !finalI) ||
            (gmud_rollback_select === 'Outros' && !finalR)
        ) {
            toast.warn('Descreva os campos "Outros".'); return;
        }
        if (new Date(form.data_fim) <= new Date(form.data_inicio)) {
            toast.warn('Data de término deve ser posterior ao início.'); return;
        }

        setSubmitting(true);
        try {
            await api.post('/api/system-hub/maintenance/schedule', {
                ...rest,
                data_inicio: new Date(rest.data_inicio).toISOString(),
                data_fim:    new Date(rest.data_fim).toISOString(),
                display_mode,
                gmud_justificativa: finalJ,
                gmud_impacto: finalI,
                gmud_rollback: finalR,
            });
            toast.success('Manutenção agendada e GMUD registrada!');
            setForm(INITIAL_FORM);
            fetchStatus();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Falha ao processar o agendamento.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelSchedule = async (id) => {
        if (!window.confirm('Cancelar esta janela de manutenção?')) return;
        setLoading(true);
        try {
            await api.delete(`/api/system-hub/maintenance/schedule/${id}`);
            toast.success('Agendamento cancelado.');
            fetchStatus();
        } catch {
            toast.error('Erro ao cancelar.');
        } finally {
            setLoading(false);
        }
    };

    // ── Render ──
    return (
        <div className="shc-root">
            {/* Header */}
            <div className="shc-page-header">
                <div>
                    <h1 className="shc-page-title"><FaTools size={20} /> Gerenciamento de Manutenção</h1>
                    <p className="shc-page-desc">Controle de infraestrutura, modo de manutenção e gerenciamento de mudanças (GMUD).</p>
                </div>
                <div className="shc-header-meta">
                    <span className={`shc-system-pill ${maintenanceStatus.isActive ? 'shc-system-pill--down' : 'shc-system-pill--up'}`}>
                        <span className="shc-system-dot" />
                        {maintenanceStatus.isActive ? 'Em Manutenção' : 'Operacional'}
                    </span>
                </div>
            </div>

            {/* ── Seção 1: Status ── */}
            <section className="shc-section">
                <SectionHeader
                    icon={FaShieldAlt}
                    title="Status em Tempo Real"
                    subtitle="O status reflete automaticamente GMUDs em execução. A ativação manual exige data/hora de término."
                />
                <StatusCard
                    status={maintenanceStatus}
                    onActivate={handleActivate}
                    onDeactivate={handleDeactivate}
                    loading={loading}
                />
            </section>

            {/* ── Seção 2: Janelas em Andamento ── */}
            <section className="shc-section">
                <SectionHeader
                    icon={FaHourglassHalf}
                    title="Janelas em Andamento"
                    subtitle="GMUDs que já iniciaram e aguardam conclusão. Saem desta lista somente após serem finalizadas."
                    badge={gmudEmAndamento.length > 0 ? `${gmudEmAndamento.length}` : null}
                />
                {loading && gmudEmAndamento.length === 0 ? (
                    <div className="shc-loading-state">
                        <FaSpinner className="shc-spin" size={18} /> <span>Sincronizando…</span>
                    </div>
                ) : gmudEmAndamento.length > 0 ? (
                    <div className="shc-schedule-list">
                        {gmudEmAndamento.map(g => (
                            <InProgressItem key={g.id} gmud={g} />
                        ))}
                    </div>
                ) : (
                    <div className="shc-empty-state">
                        <FaHourglassHalf size={28} />
                        <p>Nenhuma janela em execução no momento.</p>
                        <span>GMUDs ativas aparecerão aqui automaticamente.</span>
                    </div>
                )}
            </section>

            {/* ── Seção 3: Formulário ── */}
            <section className="shc-section">
                <SectionHeader
                    icon={FaCalendarPlus}
                    title="Agendar Nova Janela de Mudança"
                    subtitle="Registre uma GMUD e programe a manutenção com antecedência."
                />
                <form onSubmit={handleSubmit} className="shc-form">
                    <div className="shc-form-row shc-form-row--4">
                        <div className="shc-field shc-field--span2">
                            <label className="shc-label"><FaBuilding size={12} /> Escopo (cliente alvo)</label>
                            <div className="shc-select-wrap">
                                <select name="cliente_id" value={form.cliente_id} onChange={handleFormChange} disabled={submitting} className="shc-select">
                                    <option value="todos">🌐 Todos os clientes (global)</option>
                                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_empresa}</option>)}
                                </select>
                                <FaChevronDown size={11} className="shc-select-arrow" />
                            </div>
                        </div>
                        <div className="shc-field">
                            <label className="shc-label"><FaClock size={12} /> Início da janela</label>
                            <input type="datetime-local" name="data_inicio" value={form.data_inicio} onChange={handleFormChange} disabled={submitting} className="shc-input" required />
                        </div>
                        <div className="shc-field">
                            <label className="shc-label"><FaClock size={12} /> Fim previsto</label>
                            <input type="datetime-local" name="data_fim" value={form.data_fim} onChange={handleFormChange} disabled={submitting} className="shc-input" required />
                        </div>
                    </div>

                    <div className="shc-field">
                        <label className="shc-label"><FaClipboardList size={12} /> Título da mudança (motivo principal)</label>
                        <input type="text" name="motivo" value={form.motivo} onChange={handleFormChange}
                            placeholder="Ex: Upgrade de SSD nos servidores de banco de dados"
                            disabled={submitting} className="shc-input" required />
                    </div>

                    <DisplayModeToggle value={form.display_mode} onChange={handleDisplayMode} disabled={submitting} />

                    <div className="shc-gmud-block">
                        <div className="shc-gmud-block-header">
                            <FaShieldAlt size={13} /><span>Parâmetros Técnicos da GMUD</span>
                            <span className="shc-gmud-required-note">campos obrigatórios</span>
                        </div>
                        <div className="shc-gmud-grid">
                            <GmudSelect label="Justificativa da mudança" icon={FaClipboardList}
                                selectName="gmud_justificativa_select" textName="gmud_justificativa_text"
                                value={form.gmud_justificativa_select} textValue={form.gmud_justificativa_text}
                                options={GMUD_OPTIONS.JUSTIFICATIVA} onChange={handleFormChange} loading={submitting} />
                            <GmudSelect label="Impacto esperado no ambiente" icon={FaExclamationCircle}
                                selectName="gmud_impacto_select" textName="gmud_impacto_text"
                                value={form.gmud_impacto_select} textValue={form.gmud_impacto_text}
                                options={GMUD_OPTIONS.IMPACTO} onChange={handleFormChange} loading={submitting} />
                            <GmudSelect label="Plano de rollback (reversão)" icon={FaUndo}
                                selectName="gmud_rollback_select" textName="gmud_rollback_text"
                                value={form.gmud_rollback_select} textValue={form.gmud_rollback_text}
                                options={GMUD_OPTIONS.ROLLBACK} onChange={handleFormChange} loading={submitting} />
                        </div>
                    </div>

                    <div className="shc-form-footer">
                        <button type="submit" disabled={submitting} className="shc-submit-btn">
                            {submitting
                                ? <><FaSpinner className="shc-spin" size={14} /> Registrando…</>
                                : <><FaCalendarPlus size={14} /> Confirmar Agendamento e Criar GMUD</>}
                        </button>
                        <p className="shc-form-note">O agendamento criará automaticamente uma GMUD com status "Pendente".</p>
                    </div>
                </form>
            </section>

            {/* ── Seção 4: Próximas Janelas Agendadas ── */}
            <section className="shc-section">
                <SectionHeader
                    icon={FaRegCalendarAlt}
                    title="Próximas Janelas Agendadas"
                    subtitle="Manutenções programadas e aguardando execução."
                    badge={agendamentos.length > 0 ? `${agendamentos.length}` : null}
                />
                {loading && agendamentos.length === 0 ? (
                    <div className="shc-loading-state">
                        <FaSpinner className="shc-spin" size={18} /> <span>Sincronizando…</span>
                    </div>
                ) : agendamentos.length > 0 ? (
                    <div className="shc-schedule-list">
                        {agendamentos.map(ag => (
                            <ScheduleItem key={ag.id} ag={ag} clientes={clientes} onCancel={handleCancelSchedule} loading={loading} />
                        ))}
                    </div>
                ) : (
                    <div className="shc-empty-state">
                        <FaRegCalendarAlt size={28} />
                        <p>Nenhuma manutenção programada no momento.</p>
                        <span>Use o formulário acima para criar um agendamento.</span>
                    </div>
                )}
            </section>
        </div>
    );
}